import { useAuth } from "@/store/auth";
import type {
  Account,
  AccountUnread,
  Chat,
  ChatSearchHit,
  MediaOut,
  Message,
  ProfileUpdate,
  Proxy,
  ProxyBulkResponse,
  ProxyCreate,
  ProxyPoolStatus,
  SendMessageAccepted,
  SendMessageRequest,
  TokenResponse,
  UserCreate,
  UserOut,
  UserPatch,
  UUID,
} from "./types";

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown, message: string) {
    super(message);
  }
}

function baseUrl(): string {
  return useAuth.getState().base.replace(/\/+$/, "");
}

async function request<T>(
  path: string,
  opts: RequestInit & { json?: unknown; query?: Record<string, string | number | boolean | undefined> } = {}
): Promise<T> {
  const { json, query, ...rest } = opts;
  const headers = new Headers(rest.headers);
  const token = useAuth.getState().token;
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let body = rest.body;
  if (json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(json);
  }

  let url = baseUrl() + path;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += (url.includes("?") ? "&" : "?") + s;
  }

  const res = await fetch(url, { ...rest, headers, body });

  if (res.status === 401) useAuth.getState().logout();

  if (res.status === 204) return undefined as T;

  const ct = res.headers.get("content-type") ?? "";
  const payload = ct.includes("json") ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in (payload as object)
        ? (payload as { detail: unknown }).detail
        : payload;
    const msg =
      typeof detail === "string" ? detail : detail ? JSON.stringify(detail) : `HTTP ${res.status}`;
    throw new ApiError(res.status, detail, msg);
  }

  return payload as T;
}

export const api = {
  // auth
  login: (username: string, password: string) =>
    request<TokenResponse>("/auth/login", { method: "POST", json: { username, password } }),

  // users (admin only)
  listUsers: () => request<UserOut[]>("/users"),
  createUser: (body: UserCreate) => request<UserOut>("/users", { method: "POST", json: body }),
  updateUser: (id: UUID, body: UserPatch) =>
    request<UserOut>(`/users/${id}`, { method: "PATCH", json: body }),
  deleteUser: (id: UUID) => request<void>(`/users/${id}`, { method: "DELETE" }),
  getUserAccounts: (id: UUID) => request<UUID[]>(`/users/${id}/accounts`),
  setUserAccounts: (id: UUID, accountIds: UUID[]) =>
    request<UUID[]>(`/users/${id}/accounts`, { method: "PUT", json: { account_ids: accountIds } }),

  // proxies
  listProxies: () => request<Proxy[]>("/proxies"),
  createProxy: (body: ProxyCreate) => request<Proxy>("/proxies", { method: "POST", json: body }),
  bulkImportProxies: (text: string, label_prefix?: string | null) =>
    request<ProxyBulkResponse>("/proxies/bulk", {
      method: "POST",
      json: { text, label_prefix: label_prefix ?? null },
    }),
  resetProxy: (id: UUID) =>
    request<{ id: UUID; is_healthy: boolean; last_error: string | null }>(`/proxies/${id}/reset`, { method: "POST" }),
  deleteProxy: (id: UUID) => request<void>(`/proxies/${id}`, { method: "DELETE" }),
  assignProxy: (id: UUID, accountIds: UUID[]) =>
    request<Proxy>(`/proxies/${id}/assign`, { method: "POST", json: { account_ids: accountIds } }),
  proxyPoolStatus: () => request<ProxyPoolStatus>("/accounts/_pool/status"),

  // accounts
  listAccounts: () => request<Account[]>("/accounts"),
  /** Непрочитанное по всем аккаунтам разом — бейджи сайдбара и вкладки. */
  accountsUnread: () => request<AccountUnread[]>("/accounts/_unread"),
  getAccount: (id: UUID) => request<Account>(`/accounts/${id}`),
  enableAccount: (id: UUID) => request<Account>(`/accounts/${id}/enable`, { method: "POST" }),
  disableAccount: (id: UUID) => request<Account>(`/accounts/${id}/disable`, { method: "POST" }),
  reassignProxy: (id: UUID, proxyId?: UUID) =>
    request<Account>(`/accounts/${id}/reassign-proxy`, {
      method: "POST",
      ...(proxyId ? { json: { proxy_id: proxyId } } : {}),
    }),
  deleteAccount: (id: UUID) => request<void>(`/accounts/${id}`, { method: "DELETE" }),
  updateProfile: (id: UUID, body: ProfileUpdate) =>
    request<{ command_id: string }>(`/accounts/${id}/profile`, { method: "POST", json: body }),
  setAvatar: (id: UUID, media_id: UUID) =>
    request<{ command_id: string }>(`/accounts/${id}/avatar`, { method: "POST", json: { media_id } }),
  deleteAvatar: (id: UUID) =>
    request<{ command_id: string }>(`/accounts/${id}/avatar`, { method: "DELETE" }),
  lockdownPrivacy: (id: UUID) =>
    request<{ command_id: string }>(`/accounts/${id}/privacy/lockdown`, { method: "POST" }),

  // import from vendor JSON + an uploaded .session file
  importAccount: async (meta: string, sessionFile: File): Promise<Account> => {
    const fd = new FormData();
    fd.append("meta", meta);
    fd.append("session_file", sessionFile);
    return request<Account>("/accounts/import", { method: "POST", body: fd });
  },

  // import an account from the .zip the browser exporter produces
  importAccountZip: async (zipFile: File): Promise<Account> => {
    const fd = new FormData();
    fd.append("zip_file", zipFile);
    return request<Account>("/accounts/import/zip", { method: "POST", body: fd });
  },

  // chats
  /** Поиск диалога по всем аккаунтам владельца (быстрый переход ⌘K). */
  searchChats: (q: string, limit = 20) =>
    request<ChatSearchHit[]>("/chats/search", { query: { q, limit } }),
  // 500 — потолок бэкенда. Раньше брали 100: больше было нечем отрисовать,
  // теперь списки виртуализированы и лишние строки ничего не стоят.
  listChats: (accountId: UUID, limit = 500) =>
    request<Chat[]>(`/accounts/${accountId}/chats`, { query: { limit } }),
  startChat: (accountId: UUID, username: string) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/start`, {
      method: "POST",
      json: { username },
    }),
  refreshChats: (accountId: UUID) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/refresh`, { method: "POST" }),
  deleteAllChats: (accountId: UUID) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/delete-all`, { method: "POST" }),
  listMessages: (
    accountId: UUID,
    chatId: UUID,
    opts: { limit?: number; before_id?: number; include_deleted?: boolean } = {}
  ) => request<Message[]>(`/accounts/${accountId}/chats/${chatId}/messages`, { query: opts }),
  backfillChat: (accountId: UUID, chatId: UUID, limit = 100, before_id?: number) =>
    request<{ command_id: string }>(
      `/accounts/${accountId}/chats/${chatId}/backfill`,
      { method: "POST", query: { limit, before_id } }
    ),
  markRead: (accountId: UUID, chatId: UUID) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/${chatId}/read`, { method: "POST" }),
  setTyping: (accountId: UUID, chatId: UUID) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/${chatId}/typing`, { method: "POST" }),
  leaveChat: (accountId: UUID, chatId: UUID) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/${chatId}/leave`, { method: "POST" }),
  deleteDialog: (accountId: UUID, chatId: UUID) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/${chatId}/delete-dialog`, { method: "POST" }),
  blockUser: (accountId: UUID, chatId: UUID) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/${chatId}/block`, { method: "POST" }),
  bulkBlockChats: (accountId: UUID, chatIds: UUID[]) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/bulk-block`, {
      method: "POST",
      json: { chat_ids: chatIds },
    }),
  bulkDeleteChats: (accountId: UUID, chatIds: UUID[]) =>
    request<{ command_id: string }>(`/accounts/${accountId}/chats/bulk-delete`, {
      method: "POST",
      json: { chat_ids: chatIds },
    }),

  // messages
  sendMessage: (accountId: UUID, chatId: UUID, body: SendMessageRequest) =>
    request<SendMessageAccepted>(`/accounts/${accountId}/chats/${chatId}/messages`, {
      method: "POST",
      json: body,
    }),
  clickButton: (
    accountId: UUID,
    chatId: UUID,
    tgMessageId: number,
    row: number,
    col: number,
    sharePhone = false
  ) =>
    request<SendMessageAccepted>(
      `/accounts/${accountId}/chats/${chatId}/messages/${tgMessageId}/click`,
      { method: "POST", json: { row, col, share_phone: sharePhone } }
    ),

  // media
  /** XHR, а не fetch: только у XHR есть upload.onprogress, нужный для прогресс-бара
   *  вложения в композере — на больших видео/документах голого спиннера мало. */
  uploadMedia: (file: File, onProgress?: (pct: number) => void, kind?: string): Promise<MediaOut> =>
    new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append("file", file);
      if (kind) fd.append("kind", kind);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", baseUrl() + "/media");
      const token = useAuth.getState().token;
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        let payload: unknown = null;
        try {
          payload = JSON.parse(xhr.responseText);
        } catch {
          /* пустое/не-JSON тело — обработаем как ошибку ниже */
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload as MediaOut);
        } else {
          const detail =
            payload && typeof payload === "object" && "detail" in (payload as object)
              ? (payload as { detail: unknown }).detail
              : payload;
          const msg = typeof detail === "string" ? detail : detail ? JSON.stringify(detail) : `HTTP ${xhr.status}`;
          if (xhr.status === 401) useAuth.getState().logout();
          reject(new ApiError(xhr.status, detail, msg));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, null, "Сетевая ошибка"));
      xhr.send(fd);
    }),
  /** Скачать из Telegram вложение, которое не качали сразу (крупное). */
  fetchMedia: (id: UUID) =>
    request<{ command_id: string }>(`/media/${id}/fetch`, { method: "POST" }),
  mediaUrl: (id: UUID) => {
    const token = useAuth.getState().token;
    return `${baseUrl()}/media/${id}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
  }
};
